import React, { useMemo, useState } from 'react';
import atlasEmptyState from 'src/components/CategoryConfiguration/assets/atlas-empty-state.svg';
import { Button, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import { CategoryType, useGetOrganisationCategoriesQuery } from 'src/graphql';
import {
  TransformedCategory,
  transformSpeakUpCategories,
} from '../Configuration/helpers/categoryDataTransformers';
import {
  CategorySelectTriggerGroup,
  InitialStateContainer,
  InitialStateDescription,
  InitialStateIllustration,
  InitialStateTextContainer,
  InitialStateTitle,
  SectionContainer,
} from './CategorySelection.styles';
import { filterCategoriesEligibleForNewForm } from './helpers/filterCategoriesEligibleForNewForm';

interface Props {
  onCategorySelect: (category: TransformedCategory) => void;
  isCoiForm?: boolean;
  useAtlasStyle?: boolean;
  maxVisibleItems?: number;
}

export const CategorySelection = ({
  onCategorySelect,
  isCoiForm = false,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { data: organisationCategoriesData } =
    useGetOrganisationCategoriesQuery({ isCOI: isCoiForm });

  const categories = useMemo(() => {
    const eligible = filterCategoriesEligibleForNewForm(
      organisationCategoriesData?.getOrganisationCategories as
        | CategoryType[]
        | null
        | undefined,
      isCoiForm,
    );
    return transformSpeakUpCategories(eligible);
  }, [organisationCategoriesData, isCoiForm]);

  const categoryOptions = useMemo(
    () =>
      categories.map((cat) => ({
        value: String(cat.id),
        label: cat.name,
      })),
    [categories],
  );

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.currentTarget;
    setAnchorEl((prev) => (prev ? null : target));
  };

  const handleClose = () => setAnchorEl(null);

  const handleOptionClick = (value: string) => {
    const selectedCategory = categories.find((cat) => String(cat.id) === value);
    if (selectedCategory) {
      onCategorySelect(selectedCategory);
    }
    handleClose();
  };

  return (
    <SectionContainer $isInitialState data-testid="category-selection-section">
      <InitialStateContainer data-testid="category-selection-initial-state">
        <InitialStateIllustration
          src={atlasEmptyState}
          alt=""
          width={160}
          height={132}
          data-testid="category-selection-initial-illustration"
        />
        <InitialStateTextContainer>
          <InitialStateTitle data-testid="category-selection-initial-title">
            Select a category first
          </InitialStateTitle>
          <InitialStateDescription data-testid="category-selection-initial-description">
            Before you can start adding content, select your category
          </InitialStateDescription>
        </InitialStateTextContainer>
        <CategorySelectTriggerGroup data-testid="category-select-trigger-group">
          <Button
            variant="contained"
            onClick={handleOpen}
            data-testid="select-category-button"
            aria-haspopup="menu"
            aria-expanded={Boolean(anchorEl)}
            startIcon={<AddIcon />}
          >
            Select category
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            data-testid="category-dropdown-wrapper"
            disablePortal
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { maxHeight: 320 } } }}
          >
            {categoryOptions.length === 0 ? (
              <MenuItem disabled data-testid="category-options-empty">
                <ListItemText>No categories available</ListItemText>
              </MenuItem>
            ) : (
              categoryOptions.map((opt) => (
                <Tooltip
                  key={opt.value}
                  title={opt.label}
                  placement="right"
                  enterDelay={500}
                >
                  <MenuItem
                    onClick={() => handleOptionClick(opt.value)}
                    data-testid={`category-option-${opt.value}`}
                  >
                    <ListItemText
                      primary={opt.label}
                      slotProps={{
                        primary: {
                          sx: {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        },
                      }}
                    />
                  </MenuItem>
                </Tooltip>
              ))
            )}
          </Menu>
        </CategorySelectTriggerGroup>
      </InitialStateContainer>
    </SectionContainer>
  );
};
